import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, ProvedorCartoesRecolhidos, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Chatter } from '../../componentes/chatter';
import { CartaoVerticais, CampoVerticais, useVerticaisDaCriacao, verticaisDaCriacao } from '../../componentes/verticais';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dinheiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { ServicoContratado } from './tipos-catalogos';

export const CAMINHO_SERVICOS_CONTRATADOS = '/cadastros/servicos-contratados';
const CAMINHO = CAMINHO_SERVICOS_CONTRATADOS;
const ENTIDADE = 'cadastros.servico_contratado';

/** Valor do filtro de situação para o parâmetro `ativo` da API ('' = todos). */
export const ativoDoFiltro = (v: string): boolean | undefined => (v === 'ativos' ? true : v === 'inativos' ? false : undefined);

/**
 * Fase 2 (W31, 5.2): Cadastros > Serviços > Serviços, os serviços CONTRATADOS de terceiros (o que a empresa compra), ao
 * lado do Catálogo de serviços (o que a empresa vende). Mesmo padrão da tela do catálogo: Tabela com busca e filtro, Drawer
 * com o Formulario do núcleo e o chatter, lixeira, modelo e importação de CSV. A categoria de despesa vem do plano de contas
 * do Financeiro (só para quem o lê) e o fornecedor padrão é um parceiro com a marca de fornecedor; os dois são sugestão
 * para o item da compra (Compras). Arquivar é desmarcar "Ativo" (a lista filtra por situação).
 */
export function ServicosContratados() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<ServicoContratado | 'novo' | null>(null);
  const [importar, setImportar] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [situacao, setSituacao] = useState('ativos');
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useVerticaisDaCriacao(aberto);
  const podeEditar = atual ? s.pode('cadastros.editar') : s.pode('cadastros.criar');
  const leFinanceiro = s.instalado('financeiro') && s.pode('financeiro.ver');
  // Nomes das categorias de despesa para a coluna da lista (o Cadastros guarda só o id).
  const categorias = useQuery({
    queryKey: ['/financeiro/categorias', 'despesa-todas'],
    queryFn: () => getTodos<{ id: string; nome: string; codigo: string | null }>('/financeiro/categorias', { tipo: 'despesa' }),
    enabled: leFinanceiro,
    staleTime: 60_000,
  });
  const nomeCategoria = (id: string | null) => {
    if (!id) return '';
    const c = categorias.data?.itens.find((x) => x.id === id);
    return c ? [c.codigo, c.nome].filter(Boolean).join(' ') : '';
  };

  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO}/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO] });
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('contratados.campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia', max: 200, desabilitado: !podeEditar },
    { nome: 'ativo', rotulo: t('contratados.campos.ativo'), tipo: 'booleano', largura: 'terco', desabilitado: !podeEditar },
    ...(leFinanceiro
      ? ([
          {
            nome: 'categoria_despesa_id',
            rotulo: t('contratados.campos.categoria'),
            tipo: 'busca',
            largura: 'meia',
            ajuda: t('contratados.ajuda.categoria'),
            busca: { caminho: '/financeiro/categorias', params: { tipo: 'despesa', ativa: true, lancavel: true }, rotulo: (i) => [i.codigo, i.nome].filter(Boolean).join(' ') },
            desabilitado: !podeEditar,
          },
        ] satisfies DefCampo[])
      : []),
    {
      nome: 'fornecedor_padrao_id',
      rotulo: t('contratados.campos.fornecedor'),
      tipo: 'busca',
      largura: 'meia',
      ajuda: t('contratados.ajuda.fornecedor'),
      busca: { caminho: '/cadastros/parceiros', params: { papel: 'fornecedor' }, rotulo: (i) => String(i.nome) },
      desabilitado: !podeEditar,
    },
    // Lote 09/10 (B2): como no antigo; a compra sugere este valor no item.
    { nome: 'valor_referencia_centavos', rotulo: t('contratados.campos.valorReferencia'), tipo: 'dinheiro', largura: 'terco', ajuda: t('contratados.ajuda.valorReferencia'), desabilitado: !podeEditar },
    { nome: 'descricao', rotulo: t('contratados.campos.descricao'), tipo: 'area', desabilitado: !podeEditar },
  ];

  const colunas: Coluna<ServicoContratado>[] = [
    { chave: 'nome', rotulo: t('contratados.campos.nome'), principal: true, render: (x) => <span className="font-medium">{textoLivre(x.nome)}</span> },
    ...(leFinanceiro ? ([{ chave: 'categoria_despesa_id', rotulo: t('contratados.campos.categoria'), render: (x) => nomeCategoria(x.categoria_despesa_id) }] satisfies Coluna<ServicoContratado>[]) : []),
    { chave: 'fornecedor_padrao_id', rotulo: t('contratados.campos.fornecedor'), render: (x) => (x.fornecedor_nome ? nomePessoa(x.fornecedor_nome) : '') },
    { chave: 'valor_referencia_centavos', rotulo: t('contratados.campos.valorReferencia'), alinhar: 'direita', mono: true, render: (x) => (x.valor_referencia_centavos == null ? '' : dinheiro(x.valor_referencia_centavos, s.workspace.moeda)) },
    { chave: 'ativo', rotulo: t('contratados.campos.ativo'), render: (x) => (x.ativo ? <Selo cor="verde">{tn('comum.sim')}</Selo> : <Selo>{tn('comum.nao')}</Selo>) },
  ];

  return (
    <>
      {s.pode('cadastros.criar') && (
        <AcaoPrimaria>
          {s.pode('cadastros.excluir') && <BotaoLixeira entidades={[ENTIDADE]} />}
          {s.pode('cadastros.importar') && (
            <>
              <BotaoModeloCsv caminho={CAMINHO} />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importar')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('contratados.novo')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<ServicoContratado>
        tela="cadastros.servicos-contratados"
        caminho={CAMINHO}
        params={{ ativo: ativoDoFiltro(situacao) }}
        filtros={{ situacao }}
        aoAplicarFiltros={(f) => setSituacao(String(f.situacao ?? 'ativos'))}
        colunas={colunas}
        entidade={ENTIDADE}
        aoClicar={setAberto}
        vazio={<Vazio texto={t('contratados.vazio')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao
              aria-label={t('contratados.filtros.situacao')}
              opcoes={[
                { valor: 'ativos', rotulo: t('contratados.filtros.ativos') },
                { valor: 'inativos', rotulo: t('contratados.filtros.inativos') },
              ]}
              vazio={t('contratados.filtros.todos')}
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
            />
          </div>
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? textoLivre(atual.nome) : t('contratados.novo')}
        rodape={
          <>
            {atual && s.pode('cadastros.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setExcluindo(true)}>
                {t('servicos.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-servico-contratado">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-servico-contratado"
          campos={campos}
          inicial={(atual ?? { ativo: true }) as unknown as Record<string, unknown>}
          aoEnviar={async (v) => {
            const corpo: Record<string, unknown> = {
              nome: v.nome,
              descricao: (v.descricao as string) || null,
              fornecedor_padrao_id: v.fornecedor_padrao_id || null,
              valor_referencia_centavos: (v.valor_referencia_centavos as number | null | undefined) ?? null,
              ativo: v.ativo !== false,
            };
            // Sem acesso ao Financeiro a categoria nem aparece: o que já estava gravado não é tocado.
            if (leFinanceiro) corpo.categoria_despesa_id = v.categoria_despesa_id || null;
            if (atual) await patch(`${CAMINHO}/${atual.id}`, corpo);
            else await post(CAMINHO, { ...corpo, ...verticaisDaCriacao(verticais) });
            await qc.invalidateQueries({ queryKey: [CAMINHO] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        >
          {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
        </Formulario>
        {atual && (
          <div className="mt-6 border-t border-borda pt-4">
            {/* Item 10 (21/10): cartão recolhido, como na ficha de Pessoas e empresas. */}
            <ProvedorCartoesRecolhidos>
              <CartaoVerticais entidade={ENTIDADE} registroId={atual.id} podeEditar={s.pode('cadastros.editar')} />
            </ProvedorCartoesRecolhidos>
            <Chatter entidade={ENTIDADE} registroId={atual.id} titulo={atual.nome} />
          </div>
        )}
      </Drawer>
      <Confirmar
        aberto={excluindo}
        aoFechar={() => setExcluindo(false)}
        aoConfirmar={() => excluir.mutate()}
        carregando={excluir.isPending}
        perigoso
        titulo={t('contratados.excluirTitulo')}
        texto={t('contratados.excluirTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={CAMINHO} titulo={t('contratados.importar')} />
    </>
  );
}
